import { Box, Button, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useContext, useMemo, useState } from 'react';
import { IPendingContract, IResponse, ISortDirection } from '../../../models';
import { useQuery } from 'react-query';
import { getPendingContracts } from '../../../fetch';
import { MobileTable, Pagination, Table, Page, PageTitle } from '../../../components';
import {
  SALES_PORTAL_PER_PAGE_OPTIONS,
  formatShortFriendlyDateWithTime,
  getEnvironment,
  handleSortDirection
} from '../../../helpers';
import { ContractAcceptanceFilters } from './contract-acceptance-filters';
import { Article, Visibility } from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import { SalesContext, update } from '../../../context/sales';
import { GlobalContext } from '../../../context/global';
import {
  filtersPayload,
  sortingPayload,
  updateFilters,
  updateSort
} from '../../../context/actions';
import { theme } from '../../../styles/theme';

interface IContractAcceptanceFilters {
  filters?: {
    neighborhoodId?: string;
    lotNumber?: string;
    statuses?: string[];
    search?: string;
  };
}

interface IContractAcceptanceSorting {
  sort?: string;
  sortDirection?: { [key: string]: ISortDirection };
}
const PAGE_NAME = 'Contract-Acceptance';
export const ContractAcceptancePage = () => {
  const history = useHistory();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const removeStickyHeader = useMediaQuery('(max-width: 1537px)');

  const { filterValues, dispatchFilters, sortingValues, dispatchSorting } =
    useContext(GlobalContext);

  const currentFilterState = filterValues.find(state => state?.pageName === PAGE_NAME);

  const persistentFilters = useMemo<IContractAcceptanceFilters>(
    () =>
      currentFilterState
        ? (currentFilterState as IContractAcceptanceFilters)
        : ({} as IContractAcceptanceFilters),
    [currentFilterState]
  );

  const currentSortingState = sortingValues.find(state => state?.pageName === PAGE_NAME);

  const persistentSorting = useMemo<IContractAcceptanceSorting>(
    () =>
      currentSortingState
        ? (currentSortingState as IContractAcceptanceSorting)
        : ({} as IContractAcceptanceSorting),
    [currentSortingState]
  );

  const handleUpdateFilters = (payload: filtersPayload) => {
    dispatchFilters(updateFilters(payload));
  };

  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string>(
    persistentFilters?.filters?.neighborhoodId ?? ''
  );
  const [selectedLotId, setSelectedLotId] = useState<string>(
    persistentFilters?.filters?.lotNumber ?? ''
  );
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(
    persistentFilters?.filters?.statuses ?? []
  );
  const [searchedValue, setSearchedValue] = useState<string>(
    persistentFilters?.filters?.search ?? ''
  );
  const [searchValue, setSearchValue] = useState<string>(persistentFilters?.filters?.search ?? '');

  const { perPageSalesPortalValues, dispatchPerPage } = useContext(SalesContext);

  const currentPageState = perPageSalesPortalValues.find(
    state => state?.pageName === PAGE_NAME
  )?.itemsPerPage;
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const perPage = useMemo<number>(
    () => (currentPageState && currentPageState >= 25 ? currentPageState : 25),
    [currentPageState]
  );
  const selectedSort = useMemo<string>(
    () => persistentSorting.sort ?? '',
    [persistentSorting.sort]
  );
  const sortDirection = useMemo<{
    Neighborhood?: ISortDirection;
    LotNumber?: ISortDirection;
    Plan?: ISortDirection;
    ContractCompleted?: ISortDirection;
    BuildCreated?: ISortDirection;
    ContractStarted?: ISortDirection;
    Status?: ISortDirection;
    ContractName?: ISortDirection;
    JobType?: ISortDirection;
  }>(
    () =>
      persistentSorting?.sortDirection && Object.keys(persistentSorting?.sortDirection).length > 0
        ? persistentSorting?.sortDirection
        : // Value here just to make sure that the sorting is not empty
          { BuildCreated: '' },
    [persistentSorting.sortDirection]
  );

  const { isLoading, data: pendingContractsData } = useQuery<IResponse<IPendingContract[]>, Error>(
    [
      'getPendingContracts',
      page,
      perPage,
      selectedSort,
      sortDirection,
      persistentFilters,
      searchedValue
    ],
    () =>
      getPendingContracts({
        page: page + 1,
        perPage,
        neighborhoodId: selectedNeighborhoodId,
        lotNumber: selectedLotId,
        statuses: selectedStatuses,
        contractName: searchedValue,
        sortBy: selectedSort,
        sortDirection: sortDirection?.[selectedSort] as string,
        ...persistentFilters
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked',
      refetchInterval: 300000 // 5 minutes
    }
  );

  const handleEdit = buildId => {
    history.push(`/sales/contracting/contract-acceptance/${buildId}`);
  };

  const handleUpdateSorting = (payload: sortingPayload) => {
    dispatchSorting(updateSort(payload));
  };

  const data = useMemo(() => pendingContractsData?.records ?? [], [pendingContractsData]);
  const columns = useMemo(() => {
    const handleClickColumn = (column: string) => {
      const directionOfSort = handleSortDirection(sortDirection?.[column]);
      handleUpdateSorting({
        sort: directionOfSort ? column : '',
        sortDirection: {
          [column]: directionOfSort
        },
        pageName: PAGE_NAME,
        environment: getEnvironment()
      });
    };
    return [
      {
        Header: 'Build Created',
        accessor: 'buildCreated',
        isServerSorted: selectedSort === 'BuildCreated',
        isServerSortedDesc: sortDirection?.BuildCreated === 'Desc',
        handleClickColumn: () => handleClickColumn('BuildCreated'),

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPendingContract } };
        }) => {
          return (
            <>
              {original.buildCreated
                ? formatShortFriendlyDateWithTime(original.buildCreated)
                : null}
            </>
          );
        }
      },
      {
        Header: 'Contract Started',
        accessor: 'contractStarted',
        isServerSorted: selectedSort === 'ContractStarted',
        isServerSortedDesc: sortDirection?.ContractStarted === 'Desc',
        handleClickColumn: () => handleClickColumn('ContractStarted'),

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPendingContract } };
        }) => {
          return (
            <>
              {original.contractStarted
                ? formatShortFriendlyDateWithTime(original.contractStarted)
                : null}
            </>
          );
        }
      },
      {
        Header: 'Contract Completed',
        accessor: 'contractCompleted',
        isServerSorted: selectedSort === 'ContractCompleted',
        isServerSortedDesc: sortDirection?.ContractCompleted === 'Desc',
        handleClickColumn: () => handleClickColumn('ContractCompleted'),

        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPendingContract } };
        }) => {
          return (
            <>
              {original.contractCompleted
                ? formatShortFriendlyDateWithTime(original.contractCompleted)
                : null}
            </>
          );
        }
      },
      !isMobile && {
        Header: 'Neighborhood',
        accessor: 'neighborhoodName',
        isServerSorted: selectedSort === 'Neighborhood',
        isServerSortedDesc: sortDirection?.Neighborhood === 'Desc',
        handleClickColumn: () => handleClickColumn('Neighborhood')
      },
      !isMobile && {
        Header: 'Lot Number',
        accessor: 'lotNumber',
        isServerSorted: selectedSort === 'LotNumber',
        isServerSortedDesc: sortDirection?.LotNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('LotNumber')
      },
      {
        Header: 'Plan',
        accessor: 'plan',
        isServerSorted: selectedSort === 'Plan',
        isServerSortedDesc: sortDirection?.Plan === 'Desc',
        handleClickColumn: () => handleClickColumn('Plan')
      },
      {
        Header: 'Job Type',
        accessor: 'jobType',
        isServerSorted: selectedSort === 'JobType',
        isServerSortedDesc: sortDirection?.JobType === 'Desc',
        handleClickColumn: () => handleClickColumn('JobType')
      },
      !isMobile && {
        Header: 'Status',
        accessor: 'status',
        isServerSorted: selectedSort === 'Status',
        isServerSortedDesc: sortDirection?.Status === 'Desc',
        handleClickColumn: () => handleClickColumn('Status')
      },
      !isMobile && {
        Header: 'Contact Name',
        accessor: 'contractName',
        isServerSorted: selectedSort === 'ContractName',
        isServerSortedDesc: sortDirection?.ContractName === 'Desc',
        handleClickColumn: () => handleClickColumn('ContractName')
      },
      {
        Header: 'Notes',
        accessor: 'notes',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPendingContract } };
        }) => {
          return (
            <>
              {original.notes && (
                <Tooltip title={original.notes}>
                  <Article color="primary" />
                </Tooltip>
              )}
            </>
          );
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPendingContract } };
        }) => {
          return (
            <Box display="flex">
              <Button
                id="edit"
                type="button"
                color="primary"
                startIcon={<Visibility />}
                onClick={() => handleEdit(original?.buildId)}
              >
                Details
              </Button>
            </Box>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection, selectedSort, persistentFilters.filters, isMobile]);

  return (
    <Page pageHasStickyHeaders={removeStickyHeader ? false : true} title="Contract Acceptance">
      <PageTitle title="Contract Acceptance" />
      <ContractAcceptanceFilters
        isLoading={isLoading}
        selectedNeighborhoodId={selectedNeighborhoodId}
        setSelectedNeighborhoodId={setSelectedNeighborhoodId}
        selectedLotId={selectedLotId}
        setSelectedLotId={setSelectedLotId}
        selectedStatuses={selectedStatuses}
        setSelectedStatuses={setSelectedStatuses}
        searchValue={searchValue}
        searchedValue={searchedValue}
        setSearchValue={setSearchValue}
        setSearchedValue={setSearchedValue}
        handleSearch={(val: string) => {
          setPage(0);
          setSearchedValue(val);
          const payload = {
            ...persistentFilters,
            filters: {
              ...persistentFilters.filters,
              search: val
            },
            pageName: PAGE_NAME,
            environment: getEnvironment()
          };
          handleUpdateFilters(payload);
        }}
        applyFilters={(clearFilters?: boolean) => {
          if (clearFilters) {
            const payload = {
              filters: {
                neighborhoodId: '',
                lotNumber: '',
                statuses: [],
                search: ''
              },
              pageName: PAGE_NAME,
              environment: getEnvironment()
            };
            handleUpdateFilters(payload);
            setSearchValue('');
            setSearchedValue('');
          } else {
            setPage(0);
            setSearchedValue(searchValue);
            const payload = {
              ...persistentFilters,
              filters: {
                neighborhoodId: selectedNeighborhoodId,
                lotNumber: selectedLotId,
                statuses: selectedStatuses,
                search: searchValue
              },
              pageName: PAGE_NAME,
              environment: getEnvironment()
            };
            handleUpdateFilters(payload);
          }
        }}
        hasAppliedFiltersGlobal={Object.keys(persistentFilters).length !== 0}
      />
      <Table
        stickyHeaders={removeStickyHeader ? false : true}
        columns={columns}
        data={data}
        isLoading={isLoading}
        hidePagination
        mobileProps={{
          fields: columns
            .filter(c => c.Header)
            .map(c => ({
              name: c.Header,
              accessor: c.accessor,
              Cell: c?.Cell
            })),
          getAccordionHeader: (original: IPendingContract) => {
            let contractName = original.contractName;
            let status = original.status;
            return (
              <>
                {original.neighborhoodName} - Lot {original.lotNumber}
                {contractName && (
                  <>
                    <br />
                    <Typography color={theme.palette.grey[800]} variant="caption">
                      {contractName}
                    </Typography>
                  </>
                )}
                {status && (
                  <Box>
                    <Typography
                      sx={{
                        backgroundColor: theme.palette.primary.main,
                        padding: `${theme.spacing(0.25)} ${theme.spacing(0.5)}`,
                        color: theme.palette.common.white,
                        borderRadius: theme.spacing(0.25)
                      }}
                      variant="caption"
                    >
                      {status}
                    </Typography>
                  </Box>
                )}
              </>
            );
          },
          handleEdit: original => handleEdit(original?.buildId)
        }}
        ResponsiveComponent={isMobile ? MobileTable : undefined}
      />
      {!isLoading && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          rowsPerPageOptions={SALES_PORTAL_PER_PAGE_OPTIONS}
          setRowsPerPage={itemsPerPage => {
            return dispatchPerPage(
              update({
                pageName: PAGE_NAME,
                environment: getEnvironment(),
                itemsPerPage: itemsPerPage
              })
            );
          }}
        />
      )}
    </Page>
  );
};
